/**
 * Desktop Notification Provider
 *
 * Listens to Supabase Realtime events and triggers desktop notifications
 * for important events (huddle invites, client messages, etc.).
 *
 * Works with the service worker to show notifications even when the
 * browser tab is not focused or is in the background.
 */

import React, { createContext, useContext, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useDesktopNotifications, DesktopNotificationPrefs } from '@/hooks/useDesktopNotifications';
import { supabase } from '@/lib/supabase';
import { useHuddles } from '@/hooks/useHuddles';
import type { RealtimeChannel } from '@supabase/supabase-js';

interface DesktopNotificationContextType {
  // Permission state
  permission: 'default' | 'granted' | 'denied' | 'unsupported';
  isSupported: boolean;
  isEnabled: boolean;
  shouldPromptForPermission: boolean;

  // Preferences
  preferences: DesktopNotificationPrefs;
  updatePreferences: (updates: Partial<DesktopNotificationPrefs>) => void;

  // Actions
  requestPermission: () => Promise<'default' | 'granted' | 'denied' | 'unsupported'>;
}

const DesktopNotificationContext = createContext<DesktopNotificationContextType | null>(null);

export function useDesktopNotificationContext() {
  const context = useContext(DesktopNotificationContext);
  if (!context) {
    throw new Error('useDesktopNotificationContext must be used within DesktopNotificationProvider');
  }
  return context;
}

interface DesktopNotificationProviderProps {
  children: React.ReactNode;
}

export function DesktopNotificationProvider({ children }: DesktopNotificationProviderProps) {
  const { user } = useAuth();
  const {
    permission,
    isSupported,
    isEnabled,
    shouldPromptForPermission,
    preferences,
    requestPermission,
    updatePreferences,
    showHuddleInvite,
    showClientMessage,
    showTeamMessage,
  } = useDesktopNotifications();

  const { joinHuddle, declineInvite } = useHuddles();

  const channelRef = useRef<RealtimeChannel | null>(null);
  const processedInvitesRef = useRef<Set<string>>(new Set());

  // Handle notification click events from service worker
  const handleNotificationClick = useCallback((event: CustomEvent) => {
    const { action, notificationType, huddleId, url } = event.detail;

    console.log('[DesktopNotifications] Click event:', { action, notificationType, huddleId, url });

    if (notificationType === 'huddle_invite' && huddleId) {
      if (action === 'answer') {
        // Answer the huddle
        joinHuddle(huddleId).catch(console.error);
      } else if (action === 'decline') {
        // Decline the huddle
        declineInvite(huddleId).catch(console.error);
      } else {
        // Default click (no specific action) - could open UI or answer
        // For now, just focus the app - user can decide
      }
    } else if (url) {
      // Navigate to the URL - TeamHubLayout now parses ?channel= query params
      // Note: window.location.href causes a full page reload but this is acceptable
      // for notification clicks since the user is switching context anyway.
      window.location.href = url;
    }
  }, [joinHuddle, declineInvite]);

  // Listen for notification click events
  useEffect(() => {
    window.addEventListener('desktop-notification-click', handleNotificationClick as EventListener);

    return () => {
      window.removeEventListener('desktop-notification-click', handleNotificationClick as EventListener);
    };
  }, [handleNotificationClick]);

  // Subscribe to Supabase Realtime for huddle invites
  useEffect(() => {
    if (!user || !isEnabled) return;

    // Clean up existing channel
    if (channelRef.current) {
      supabase.removeChannel(channelRef.current);
      channelRef.current = null;
    }

    // Subscribe to huddle participant insertions for this user
    const channel = supabase
      .channel('desktop-notifications-huddles')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'huddle_participants',
        },
        async (payload) => {
          const data = payload.new as {
            id: string;
            huddle_id: string;
            user_id: string;
            invited_at: string;
            joined_at: string | null;
            is_initiator: boolean;
          };

          // Only process invites for current user
          if (data.user_id !== user.id) return;

          // Skip if user is the initiator (they don't get notified of their own huddle)
          if (data.is_initiator) return;

          // Skip if already joined
          if (data.joined_at) return;

          // Skip if we already processed this invite (prevents duplicates)
          if (processedInvitesRef.current.has(data.id)) return;
          processedInvitesRef.current.add(data.id);

          // Limit the set size to prevent memory leaks
          if (processedInvitesRef.current.size > 100) {
            const entries = Array.from(processedInvitesRef.current);
            processedInvitesRef.current = new Set(entries.slice(-50));
          }

          console.log('[DesktopNotifications] New huddle invite:', data);

          // Fetch huddle details to get initiator name
          try {
            const { data: huddle, error } = await supabase
              .from('huddles')
              .select(`
                id,
                huddle_type,
                initiated_by,
                team_hub_users!huddles_initiated_by_fkey(
                  full_name,
                  display_name
                )
              `)
              .eq('id', data.huddle_id)
              .single();

            if (error || !huddle) {
              console.error('[DesktopNotifications] Failed to fetch huddle details:', error);
              return;
            }

            // Get initiator name
            const initiatorUser = (huddle as any).team_hub_users;
            const initiatorName = initiatorUser?.display_name || initiatorUser?.full_name || 'Someone';

            // Show desktop notification
            showHuddleInvite(
              initiatorName,
              data.huddle_id,
              huddle.huddle_type as 'audio' | 'video'
            );
          } catch (error) {
            console.error('[DesktopNotifications] Error processing huddle invite:', error);
          }
        }
      );

    // Subscribe
    channel.subscribe((status) => {
      if (status === 'SUBSCRIBED') {
        console.log('[DesktopNotifications] Subscribed to huddle invites');
      } else if (status === 'CHANNEL_ERROR') {
        console.error('[DesktopNotifications] Error subscribing to huddle invites');
      }
    });

    channelRef.current = channel;

    return () => {
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
        channelRef.current = null;
      }
    };
  }, [user, isEnabled, showHuddleInvite]);

  // Subscribe to client messages (from client Slack channels)
  useEffect(() => {
    if (!user || !isEnabled) return;
    if (preferences.clientMessages === 'never') return;

    const channel = supabase
      .channel('desktop-notifications-client-messages')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'client_message_intel',
        },
        async (payload) => {
          const data = payload.new as {
            id: string;
            client_slug: string;
            sender_name: string;
            summary: string;
            message_timestamp: string;
            slack_channel_id: string;
          };

          // Check if we should show based on preference
          if (preferences.clientMessages === 'when_away' && !document.hidden) {
            return;
          }

          console.log('[DesktopNotifications] New client message:', data);

          // Format client name (convert slug to title case)
          const clientName = data.client_slug
            .split('_')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');

          // Show notification
          showClientMessage(
            clientName,
            data.sender_name,
            data.summary || 'New message',
            data.slack_channel_id
          );
        }
      );

    channel.subscribe((status) => {
      if (status === 'SUBSCRIBED') {
        console.log('[DesktopNotifications] Subscribed to client messages');
      }
    });

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user, isEnabled, preferences.clientMessages, showClientMessage]);

  // Provide context
  const contextValue: DesktopNotificationContextType = {
    permission,
    isSupported,
    isEnabled,
    shouldPromptForPermission,
    preferences,
    updatePreferences,
    requestPermission,
  };

  return (
    <DesktopNotificationContext.Provider value={contextValue}>
      {children}
    </DesktopNotificationContext.Provider>
  );
}

export default DesktopNotificationProvider;
